const MEMORY_SERVER_URL = import.meta.env.VITE_MEMORY_SERVER_URL || 'http://localhost:3007';

interface MCPRequest {
  jsonrpc: string;
  id: number;
  method?: string;
  params?: {
    name: string;
    arguments: any;
  };
  context?: {
    user_id: string;
  };
}

interface MCPResponse {
  jsonrpc: string;
  id: number;
  result?: {
    content: { type: string; text: string }[];
  };
  error?: {
    code: number;
    message: string;
  };
}

class MemoryService {
  private requestId = 1;

  private async makeRequest(toolName: string, args: any = {}, userId?: string): Promise<any> {
    const request: MCPRequest = {
      jsonrpc: '2.0',
      id: this.requestId++,
      params: {
        name: toolName,
        arguments: args
      }
    };

    // Add user context if provided
    if (userId) {
      request.context = { user_id: userId };
    }

    try {
      const response = await fetch(`${MEMORY_SERVER_URL}/tools/call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const mcpResponse: MCPResponse = await response.json();

      if (mcpResponse.error) {
        throw new Error(`MCP Error: ${mcpResponse.error.message}`);
      }

      // Parse the text content as JSON
      if (mcpResponse.result?.content?.[0]?.text) {
        return JSON.parse(mcpResponse.result.content[0].text);
      }

      return null;
    } catch (error) {
      console.error(`Memory service error for ${toolName}:`, error);
      throw error;
    }
  }

  // Get all memory types (working, episodic, long-term)
  async getAllMemories(userId: string) {
    try {
      const result = await this.makeRequest('get_all_memories', {}, userId);
      return result?.memories || null;
    } catch (error) {
      console.error('Error fetching memories:', error);
      return null;
    }
  }

  // Get mind map data for visualization
  async getMindMap(userId: string) {
    try {
      const result = await this.makeRequest('get_mind_map', {}, userId);
      return result?.mindMap?.points || [];
    } catch (error) {
      console.error('Error fetching mind map:', error);
      return [];
    }
  }

  // Get relevant messages using semantic search
  async getRelevantMessages(userId: string, query: string, limit: number = 20) {
    try {
      const result = await this.makeRequest('get_relevant_messages', { query, limit }, userId);
      return result?.messages || [];
    } catch (error) {
      console.error('Error fetching relevant messages:', error);
      return [];
    }
  }

  // Save a message to memory system
  async saveMessage(userId: string, message: {
    role: 'user' | 'assistant';
    model?: string;
    text: string;
    conversationId: string;
    timestamp?: number;
  }) {
    try {
      const result = await this.makeRequest('save_message', {
        role: message.role,
        model: message.model || 'unknown',
        text: message.text,
        conversationId: message.conversationId,
        timestamp: message.timestamp || Date.now()
      }, userId);
      return result;
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  }

  // Generate/regenerate mind map
  async generateMindMap(userId: string) {
    try {
      const result = await this.makeRequest('generate_mind_map', {}, userId);
      return result;
    } catch (error) {
      console.error('Error generating mind map:', error);
      throw error;
    }
  }

  // Get user profile
  async getUserProfile(userId: string) {
    try {
      const result = await this.makeRequest('get_user_profile', {}, userId);
      return result?.profile || null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  // Get conversation messages
  async getConversationMessages(userId: string, conversationId: string, limit: number = 50) {
    try {
      const result = await this.makeRequest('get_conversation_messages', { 
        conversationId, 
        limit 
      }, userId);
      return result?.messages || [];
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      return [];
    }
  }

  // Fetch comprehensive memory data for a query
  async getMemoryDataForQuery(userId: string, query?: string) {
    try {
      const [memories, mindMap, relevantMessages] = await Promise.all([
        this.getAllMemories(userId),
        this.getMindMap(userId),
        query ? this.getRelevantMessages(userId, query, 10) : Promise.resolve([])
      ]);

      return {
        memories,
        mindMap,
        relevantMessages,
        query
      };
    } catch (error) {
      console.error('Error fetching comprehensive memory data:', error);
      return {
        memories: null,
        mindMap: [],
        relevantMessages: [],
        query
      };
    }
  }

  // Check if memory server is available
  async checkHealth() {
    try {
      const response = await fetch(`${MEMORY_SERVER_URL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.status === 'healthy';
      }
      return false;
    } catch (error) {
      console.warn('Memory server health check failed:', error);
      return false;
    }
  }
}

export const memoryService = new MemoryService(); 